import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Info, ChevronDown } from 'lucide-react';
export function BusinessModel() {
  const [showAssumptions, setShowAssumptions] = useState(false);
  return <section id="pricing" className="py-24 border-b border-stone-300" data-id="element-734">
      <div className="max-w-6xl mx-auto px-6" data-id="element-735">
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-736">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16" data-id="element-737">
            {/* Left: Header */}
            <div className="lg:col-span-5" data-id="element-738">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-739">
                Business Model
              </p>

              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.1] text-stone-900 mb-6" data-id="element-740">
                We win when you win
              </h2>

              <p className="text-2xl text-red-600 font-mono mb-6" data-id="element-741">
                5% of verified net savings
              </p>

              <p className="text-lg text-stone-600 leading-relaxed mb-8" data-id="element-742">
                We deploy the platform free or near-free. We establish a
                baseline from your trailing 12 months. We measure the wholesale
                leakage eliminated. We take 5% of the verified net savings.
                That's it.
              </p>

              <div className="space-y-3 text-sm text-stone-500" data-id="element-743">
                <div className="flex items-start gap-2" data-id="element-744">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-stone-400" data-id="element-745" />
                  <span data-id="element-746">
                    <strong className="text-stone-700" data-id="element-747">Savings</strong> = Group
                    Room Revenue × Wholesale Leakage % × Adoption %
                  </span>
                </div>
                <div className="flex items-start gap-2" data-id="element-748">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-stone-400" data-id="element-749" />
                  <span data-id="element-750">
                    <strong className="text-stone-700" data-id="element-751">BQ Share</strong> =
                    Savings × 5%
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Financial breakdown */}
            <div className="lg:col-span-7" data-id="element-752">
              <div className="border border-stone-300" data-id="element-753">
                <div className="p-6 border-b border-stone-300 bg-stone-100" data-id="element-754">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-755">
                    Financial Example — Base Case
                  </p>
                </div>

                {/* Inputs */}
                <div className="p-6 border-b border-stone-300" data-id="element-756">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-757">
                    Inputs
                  </p>
                  <div className="grid grid-cols-3 gap-4 font-mono text-sm" data-id="element-758">
                    <div data-id="element-759">
                      <p className="text-stone-500 mb-1" data-id="element-760">Group Room Revenue</p>
                      <p className="text-stone-900 text-lg" data-id="element-761">$50M</p>
                      <p className="text-xs text-stone-400 mt-1" data-id="element-762">
                        Annual group bookings
                      </p>
                    </div>
                    <div data-id="element-763">
                      <p className="text-stone-500 mb-1" data-id="element-764">Wholesale Leakage</p>
                      <p className="text-stone-900 text-lg" data-id="element-765">20%</p>
                      <p className="text-xs text-stone-400 mt-1" data-id="element-766">
                        Revenue lost to wholesalers
                      </p>
                    </div>
                    <div data-id="element-767">
                      <p className="text-stone-500 mb-1" data-id="element-768">Adoption</p>
                      <p className="text-stone-900 text-lg" data-id="element-769">60%</p>
                      <p className="text-xs text-stone-400 mt-1" data-id="element-770">
                        Volume routed via BlockQuote
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-stone-200" data-id="element-771">
                    <p className="font-mono text-xs text-stone-400 mb-2" data-id="element-772">
                      $50M × 20% × 60% =
                    </p>
                    <p className="font-mono text-2xl text-stone-900" data-id="element-773">
                      $6.0M{' '}
                      <span className="text-sm text-stone-500" data-id="element-774">
                        estimated annual savings
                      </span>
                    </p>
                  </div>
                </div>

                {/* With BlockQuote */}
                <div className="p-6 border-b border-stone-300" data-id="element-775">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-4" data-id="element-776">
                    With BlockQuote
                  </p>
                  <div className="grid grid-cols-2 gap-8" data-id="element-777">
                    <div data-id="element-778">
                      <p className="font-mono text-xs text-stone-500 mb-2" data-id="element-779">
                        Resort Keeps (95%)
                      </p>
                      <p className="font-mono text-3xl text-stone-900" data-id="element-780">$5.7M</p>
                      <p className="text-sm text-stone-500 mt-1" data-id="element-781">
                        Pure profit to bottom line
                      </p>
                    </div>
                    <div data-id="element-782">
                      <p className="font-mono text-xs text-stone-500 mb-2" data-id="element-783">
                        BlockQuote Fee (5%)
                      </p>
                      <p className="font-mono text-3xl text-red-600" data-id="element-784">$300K</p>
                      <p className="text-sm text-stone-500 mt-1" data-id="element-785">
                        Only on verified savings
                      </p>
                    </div>
                  </div>
                </div>

                {/* Note */}
                <div className="p-4 bg-stone-50" data-id="element-786">
                  <p className="font-mono text-xs text-stone-500" data-id="element-787">
                    * Example per resort group. Scales with portfolio size.
                  </p>
                </div>
              </div>

              {/* Assumptions Toggle */}
              <button onClick={() => setShowAssumptions(!showAssumptions)} className="mt-4 flex items-center gap-2 text-sm text-stone-500 hover:text-stone-700 transition-colors" data-id="element-788">
                <ChevronDown className={`w-4 h-4 transition-transform ${showAssumptions ? 'rotate-180' : ''}`} data-id="element-789" />
                Assumptions
              </button>
              {showAssumptions && <div className="mt-3 p-4 bg-stone-50 border border-stone-200 text-sm text-stone-500 space-y-2" data-id="element-790">
                  <p data-id="element-791">
                    • Savings are calculated from verified leakage reduction on
                    group room revenue.
                  </p>
                  <p data-id="element-792">
                    • Adoption % reflects the portion of group volume routed
                    through BlockQuote.ai.
                  </p>
                  <p data-id="element-793">
                    • Actual results vary by seasonality, segment, and
                    integration depth.
                  </p>
                </div>}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}